import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAppUserStore } from '@/modules/app-user/stores/app-user.store'

/**
 * Creates a navigation guard that checks if user has the required permission
 * @param permission - Permission string to check (e.g., 'station.view')
 * @returns Navigation guard function
 */
export const checkPermission = (permission: string) => {
  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    const userStore = useAppUserStore()

    if (!userStore.hasPermission(permission)) {
      // Redirect to access denied page
      return next({ name: 'auth.access' })
    }

    next()
  }
}

/**
 * Checks if user has ANY of the provided permissions
 */
export const checkAnyPermission = (permissions: string[]) => {
  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    const userStore = useAppUserStore()

    const hasAnyPermission = permissions.some(permission => userStore.hasPermission(permission))

    if (!hasAnyPermission) {
      return next({ name: 'auth.access' })
    }

    next()
  }
}

/**
 * Checks if user has ALL of the provided permissions
 */
export const checkAllPermissions = (permissions: string[]) => {
  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    const userStore = useAppUserStore()

    const hasAllPermissions = permissions.every(permission => userStore.hasPermission(permission))

    if (!hasAllPermissions) {
      return next({ name: 'auth.access' })
    }

    next()
  }
}

/**
 * Checks if user has a specific role
 */
export const checkRole = (role: string) => {
  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    const userStore = useAppUserStore()

    if (!userStore.hasRole(role)) {
      return next({ name: 'auth.access' })
    }

    next()
  }
}

/**
 * Checks if user is admin or super-admin
 */
export const checkIsAdminOrSuperAdmin = () => {
  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    const userStore = useAppUserStore()

    if (!userStore.isAdminOrSuperAdmin) {
      return next({ name: 'auth.access' })
    }

    next()
  }
}
